import { Divider, Flex, Text, useToast, Box, Button } from "@chakra-ui/react";
import EvaluationScreenSkeleton from "components/EvaluationScreen/Skeleton/EvaluationScreenSkeleton";
import QuestionTypes from "constants/QuestionTypes";
import { genericErrorMessage } from "messages/errorMessages";
import { useContext, useEffect, useMemo, useState } from "react";
import { post } from "utils/fetch";
import { regexMarkFinder } from "utils/regexMarkFinder";
import { fetchUserData } from "utils/user";
import ContainerLayout from "../ContainerLayout";
import CorrectionProgress from "../CorrectionProgress";
import { EvaluationActions, EvaluationContext } from "./MyEvaluationContext";
import MyEvaluationGeneralComment from "./MyEvaluationGeneralComment";
import MyEvaluationMultipleChoice from "./MyEvaluationMultipleChoice";
import MyEvaluationNavigationButtons from "./MyEvaluationNavigationButtons";
import MyEvaluationParagraphText from "./MyEvaluationParagraphText";
import MyEvaluationScore from "./MyEvaluationScore";
import MyEvaluationTitle from "./MyEvaluationTitle";
import MyEvaluationTable from "./MyEvaluationTable";
import { useTranslation } from "react-i18next";
import { FaWandMagicSparkles } from "react-icons/fa6";
import { useNavigationCorrection } from "hooks/useNavigationCorrection";
import { llmCorrectionSuggestion } from "service/LlmServices";

import { v4 as uuidv4 } from "uuid";

const MyEvaluationContent = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const {
    errorMessage,
    setRecommendedTags,
    dispatch,
    evaluationQuestionDescription,
    setGeneralComment,
  } = useContext(EvaluationContext);
  const {
    responseId,
    questionId,
    workId,
    classroomId,
    setListEvaluation,
    setFilterBy,
    setQuestionType,
  } = useNavigationCorrection();
  const { formId, init, questionType, tags } = useContext(EvaluationContext);

  const handleSaveStartTime = () => {
    post(
      "api/courses/correction/",
      {
        question_id: questionId,
        answer_id: responseId,
        started_at: new Date(),
      },
      "PUT"
    );
  };

  const QuestionContent = useMemo(() => {
    const types = {
      [QuestionTypes.PARAGRAPH_TEXT]: MyEvaluationParagraphText,
      [QuestionTypes.TEXT]: MyEvaluationParagraphText,
      [QuestionTypes.MULTIPLE_CHOICE]: MyEvaluationMultipleChoice,
    };

    return types[questionType] || (() => <></>);
  }, [questionType]);

  useEffect(() => {
    if (errorMessage !== "") {
      if (!toast.isActive(classroomId)) {
        toast({
          classroomId,
          description: genericErrorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage]);

  useEffect(() => {
    post("api/courses/details/", {
      view: window.localStorage.getItem("viewOption"),
      work_id: workId,
    }).then((res) => {
      let data = res.data;
      setFilterBy(window.localStorage.getItem("viewOption"));
      setListEvaluation(data);
    });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchUserData().then((r) => {
      post("api/courses/correction/", {
        response_id: responseId,
        question_id: questionId,
      }).then((res) => {
        if (res.data.recommended_tags) {
          res?.data?.recommended_tags?.forEach((tag) => {
            const initIndex = regexMarkFinder(
              tag.content_to_mark,
              res.data.answer
            );
            tag.init = initIndex || 0;
            tag.end = initIndex + tag.content_to_mark?.length || 0;
          });
        }
        init(r, res);
        setQuestionType(res.data.question_type);
        setIsLoading(false);
      });
    });
    handleSaveStartTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init, questionId, responseId]);

  const saveTagsForUser = (tagId, isAccepted) => {
    post("api/courses/correction/ai_tags", {
      question_id: questionId,
      answer_id: responseId,
      tag_id: tagId,
      accepted: isAccepted,
    });
  };

  const suggestCorrection = async () => {
    try {
      setLoading(true);
      const response = await llmCorrectionSuggestion(questionId, responseId);

      setLoading(false);
      response.tags.forEach((item) => {
        const start = evaluationQuestionDescription.indexOf(item.trecho);
        const end = start + item.trecho.length;
        const payload = {
          end: end,
          init: start,
          content: item.trecho,
          color: item.color,
          type: item.tipo,
          tag_name: item.titulo,
          explanation: item.feedback,
          isRecommendation: false,
          id: uuidv4(),
        };
        dispatch({
          type: EvaluationActions.addRange,
          payload,
        });
      });

      dispatch({
        type: EvaluationActions.updateEvaluation,
        payload: { score: response.nota_sugerida },
      });
      setGeneralComment(response.justificativa);
    } catch (error) {
      setIsLoading(false);
      toast({
        title: t("ERROR"),
        status: "error",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
    }
  };

  if (isLoading)
    return (
      <ContainerLayout>
        <EvaluationScreenSkeleton />
      </ContainerLayout>
    );
  return (
    <Flex spacing="4" direction="row" marginLeft="40px">
      <ContainerLayout>
        <Flex justify="space-between" alignItems="center" width="100%">
          <MyEvaluationTitle />
          <CorrectionProgress
            id={classroomId}
            activityId={workId}
            formId={formId}
            responseId={responseId}
            topN={2}
            percentageQuestions={50}
            percentageStudents={20}
            percentagePoints={70}
            position="sticky"
            marginTop={4}
          />
        </Flex>
        <Divider marginTop="2" borderColor="gray.400" />
        <Flex width="100%" justify="space-between">
          <QuestionContent />
          {questionType === QuestionTypes.PARAGRAPH_TEXT && (
            <Flex direction="column" width="28%">
              <Box className="box">
                <Button
                  onClick={suggestCorrection}
                  isLoading={loading}
                  marginLeft={"auto"}
                  marginBottom={6}
                  rightIcon={<FaWandMagicSparkles />}
                >
                  {t("CORRECT_WITH_IA")}
                </Button>
                <MyEvaluationScore />
                <MyEvaluationGeneralComment
                  responseID={responseId}
                  questionID={questionId}
                />
              </Box>

              <MyEvaluationTable
                title={
                  <Flex alignItems="center">
                    <Text mr={2}>{t("AI_SUGGESTION")}</Text>
                    <FaWandMagicSparkles />
                  </Flex>
                }
                tags={tags.filter((item) => item.isRecommendation)}
                removeFeedback={true}
                tooltipText={t("ACCEPT_SUGGESTION")}
                onAdd={(tag) => {
                  const payload = {
                    ...tag,
                    recommendation_accepted: true,
                  };
                  setRecommendedTags((state) =>
                    state.filter((r) => tag.id !== r.id)
                  );
                  dispatch({
                    type: EvaluationActions.addRange,
                    payload,
                  });
                }}
                onDelete={(tag) => {
                  setRecommendedTags((state) =>
                    state.filter((r) => tag.id !== r.id)
                  );
                  saveTagsForUser(tag.id, false);
                }}
                descWarning={t("AI_SUGGESTION_WARNING")}
                titleHelpPopover={t("WHAT_IS_AI_SUGGESTION?.TITLE")}
                descriptionHelpPopover={t("WHAT_IS_AI_SUGGESTION?.DESCRIPTION")}
              />
              <MyEvaluationNavigationButtons />
            </Flex>
          )}
        </Flex>
      </ContainerLayout>
    </Flex>
  );
};

export default MyEvaluationContent;
